/**
 * Canonical selectors for unwrapping generated Orval responses.
 *
 * Generated query functions return the full `{ data, meta }` envelope. These
 * helpers expose domain data without page-specific nested casts and reject
 * malformed responses before a component renders misleading empty content.
 */
import type { ApiResponseMeta, ApiSuccessEnvelope } from '@finance/contracts';

export type PagedData<T> = {
  items: T[];
  pageInfo: CursorPageInfo;
};

export interface CursorPageInfo {
  hasNextPage: boolean;
  nextCursor?: string;
  previousCursor?: string;
  totalCount?: number;
}

export interface TableSurface<T> {
  table: { rows: T[] };
}

export interface SurfaceEnvelope<T> {
  data: TableSurface<T>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Unwrap the canonical success envelope when the type is already known. */
export function selectApiData<T>(response: ApiSuccessEnvelope<T>): T {
  return response.data;
}

/** Safely unwrap an unknown generated-client response. */
export function selectGeneratedApiData<T>(response: unknown): T {
  if (!isRecord(response) || !('data' in response)) {
    throw new Error('API_ENVELOPE_MALFORMED');
  }
  return response.data as T;
}

/** Safely unwrap a list or canonical `{ items }` collection. */
export function selectGeneratedApiList<T>(response: unknown): T[] {
  const data = selectGeneratedApiData<unknown>(response);
  if (Array.isArray(data)) return data as T[];
  if (isRecord(data) && Array.isArray(data.items)) return data.items as T[];
  throw new Error('API_LIST_MALFORMED');
}

/** Unwrap a paginated payload. */
export function selectPagedItems<T>(response: ApiSuccessEnvelope<PagedData<T>>): PagedData<T> {
  return response.data;
}

/** Unwrap surface descriptor rows from a `{ data: { table: { rows } } }` envelope. */
export function selectSurfaceRows<T>(response: ApiSuccessEnvelope<TableSurface<T>>): T[] {
  const rows = response.data?.table?.rows;
  if (!Array.isArray(rows)) throw new Error('SURFACE_ROWS_MALFORMED');
  return rows;
}

/** Unwrap a surface envelope where the payload contains `{ data: { table: { rows } } }`. */
export function selectNestedSurfaceRows<T>(response: ApiSuccessEnvelope<SurfaceEnvelope<T>>): T[] {
  const rows = response.data?.data?.table?.rows;
  if (!Array.isArray(rows)) throw new Error('NESTED_SURFACE_ROWS_MALFORMED');
  return rows;
}

export type { ApiResponseMeta, ApiSuccessEnvelope };
